import { Button, CloseIcon, Dialog, DuplicateIcon, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganization } from '../../../hooks/useOrganizations';
import { useUser } from '../../../hooks/useUser';
import { TextingLine, useTextingLine } from '../../../integrations/marketplace/useTextingLine';
import { logWarning } from '../../../rollbar';
import { SEGMENT_EVENTS, useSegmentTrack } from '../../../segment';
import BealeChatDemo from './BealeChatDemo';
import { isTextCapableDevice, openSmsLink, usePrefersReducedMotion } from './device';

export function dismissalStorageKey(uid: string, organizationId: string): string {
  return `bealeTextingCard:dismissed:${uid}:${organizationId}`;
}

function readDismissed(key: string | null): boolean {
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeDismissed(key: string | null): void {
  if (!key) return;
  try {
    window.localStorage.setItem(key, 'true');
  } catch {
    // Storage unavailable (private mode, blocked site data). Dismissal still
    // holds for this session through component state.
  }
}

/**
 * Dashboard promo for Beale, Wonderschool's texting assistant. Carriers require
 * the provider to text the shared line first, so this card is how a director
 * finds out the line exists. Nova's /firebase-api/texting-line is the only gate:
 * it decides who is eligible, and its FunWithFlags flag (set in Endor) turns the
 * card on per school or for everyone. Off there means `show: false` here.
 */
export default function BealeTextingCard() {
  const { t } = useTranslation();
  const { isParent, uid } = useUser();
  const organization = useOrganization();
  const segmentTrack = useSegmentTrack();
  const reducedMotion = usePrefersReducedMotion();
  const textingLine = useTextingLine({ enabled: !isParent });

  const storageKey = uid && organization?.id ? dismissalStorageKey(uid, organization.id) : null;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const isDismissed = useMemo(
    () => (storageKey ? dismissedKey === storageKey || readDismissed(storageKey) : false),
    [dismissedKey, storageKey]
  );

  const [playKey, setPlayKey] = useState(0);
  const [isNumberDialogOpen, setNumberDialogOpen] = useState(false);

  const line = textingLine.status === 'ready' ? textingLine.line : null;
  const isVisible = !isParent && !!line && !isDismissed;
  // The full card shows until it is dismissed, whether or not the school has
  // texted yet; has_texted rides along on the events instead.
  const hasTexted = !!line?.has_texted;

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    segmentTrack(SEGMENT_EVENTS.bealeTextingCardViewed, { hasTexted });
  }, [isVisible, segmentTrack, hasTexted]);

  const onDismiss = useCallback(() => {
    writeDismissed(storageKey);
    setDismissedKey(storageKey);
    segmentTrack(SEGMENT_EVENTS.bealeTextingCardDismissed, { hasTexted });
  }, [segmentTrack, storageKey, hasTexted]);

  const onReplay = useCallback(() => {
    setPlayKey((key) => key + 1);
    segmentTrack(SEGMENT_EVENTS.bealeTextingCardDemoPlayed);
  }, [segmentTrack]);

  const onText = useCallback(() => {
    if (!line) return;
    const isMobile = isTextCapableDevice();
    segmentTrack(SEGMENT_EVENTS.bealeTextingCardTextClicked, { device: isMobile ? 'mobile' : 'desktop' });
    if (isMobile) {
      openSmsLink(line.sms_link);
    } else {
      setNumberDialogOpen(true);
    }
  }, [line, segmentTrack]);

  if (!isVisible || !line) return null;

  const dismissButton = (
    <button
      type="button"
      aria-label={t('dashboard.bealeTextingCard.dismissAction')}
      className="text-gray-400 hover:text-gray-600"
      onClick={onDismiss}
      data-testid="beale-texting-card-dismiss"
    >
      <CloseIcon className="size-5" />
    </button>
  );

  return (
    <div
      className="relative flex gap-6 rounded-lg border border-blue-200 bg-blue-50 p-5"
      data-testid="beale-texting-card"
    >
      <div className="absolute right-3 top-3">{dismissButton}</div>

      <div className="flex min-w-0 flex-1 flex-col justify-center pr-6">
        <span className="mb-2 inline-flex w-fit rounded-full bg-blue-900/10 px-2 py-0.5 text-xs font-semibold text-blue-900">
          {t('dashboard.bealeTextingCard.newLabel')}
        </span>
        <h3 className="text-base font-semibold text-navy-950">{t('dashboard.bealeTextingCard.title')}</h3>
        <p className="mt-0.5 text-sm text-gray-600">{t('dashboard.bealeTextingCard.description')}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button primary size={WidgetSizeEnum.SMALL} onClick={onText} data-testid="beale-texting-card-text">
            {t('dashboard.bealeTextingCard.textAction')}
          </Button>
          {/* Same breakpoint as the demo panel: the replay has nothing to show without it. */}
          <span className="hidden md:inline-flex" data-testid="beale-texting-card-replay-wrapper">
            <Button size={WidgetSizeEnum.SMALL} onClick={onReplay} data-testid="beale-texting-card-replay">
              {t('dashboard.bealeTextingCard.demoAction')}
            </Button>
          </span>
        </div>

        <p className="mt-3 text-xs text-gray-500" data-testid="beale-texting-card-fine-print">
          {t('dashboard.bealeTextingCard.finePrint', { displayNumber: line.display_number })}
        </p>
      </div>

      {/* Right margin leaves a gutter for the close button, which is pinned to the card corner. */}
      <div className="hidden flex-none md:mr-8 md:block">
        <BealeChatDemo playKey={playKey} reducedMotion={reducedMotion} />
      </div>

      <BealeNumberDialog line={line} isOpen={isNumberDialogOpen} onClose={() => setNumberDialogOpen(false)} />
    </div>
  );
}

type BealeNumberDialogProps = {
  line: TextingLine;
  isOpen: boolean;
  onClose: () => void;
};

function BealeNumberDialog({ line, isOpen, onClose }: BealeNumberDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) setCopied(false);
  }, [isOpen]);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(line.number);
      setCopied(true);
    } catch (error) {
      logWarning('BealeTextingCard: clipboard write failed', { error });
    }
  }, [line.number]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} hasCloseButton data-testid="beale-number-dialog">
      <Dialog.Panel>
        <Dialog.Title>
          {t('dashboard.bealeTextingCard.dialog.title', { displayNumber: line.display_number })}
        </Dialog.Title>
        <div className="mt-4 flex flex-col items-center gap-4">
          <p className="text-3xl font-semibold tracking-wide text-navy-950" data-testid="beale-number-dialog-number">
            {line.display_number}
          </p>
          <Button size={WidgetSizeEnum.SMALL} onClick={onCopy} data-testid="beale-number-dialog-copy">
            <span className="flex items-center gap-1.5">
              <DuplicateIcon className="h-4 w-4" />
              {copied
                ? t('dashboard.bealeTextingCard.dialog.copiedLabel')
                : t('dashboard.bealeTextingCard.dialog.copyAction')}
            </span>
          </Button>
          <div className="flex flex-col items-center gap-2 pt-2">
            <QRCodeSVG value={line.sms_link} size={128} includeMargin />
            <p className="text-xs text-gray-500">{t('dashboard.bealeTextingCard.dialog.qrCaption')}</p>
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
}
